import type React from 'react'
import type { Transform } from '@dnd-kit/utilities'
import {
  Activators,
  ClientRect,
  CollisionDetection,
  DragMoveEvent,
  KeyboardCode,
  KeyboardCodes,
  KeyboardCoordinateGetter,
  KeyboardSensor,
  KeyboardSensorOptions,
  Modifier,
  PointerSensor,
  PointerSensorOptions,
  closestCenter,
  pointerWithin
} from '@dnd-kit/core'
import { DropZone } from 'components/sidebar/components/folders/tree'
import { TREE_DROP_ID, zoneFor } from 'components/sidebar/components/folders/dnd'

/**
 * The parts of the sidebar's drag and drop that deal with dnd-kit and the page rather than the tree:
 * sensors, where a drag is, keyboard steps, and the page listeners a drag needs while it's on.
 *
 * Touch dragging isn't supported. The PointerSensor does start on a touch, but the sidebar scrolls
 * under a finger, so the browser cancels the pointer before the 6px threshold is reached. On touch
 * devices, "Move to…" in the context menu is the way to move things.
 */

const POINTER_OPTIONS: PointerSensorOptions = {
  // Clicks and double-clicks on a row stay clicks until the pointer has moved this far.
  activationConstraint: { distance: 6 }
}

const KEYBOARD_CODES: KeyboardCodes = {
  start: [KeyboardCode.Up, KeyboardCode.Down],
  cancel: [KeyboardCode.Esc],
  end: [KeyboardCode.Enter, 'NumpadEnter', KeyboardCode.Space]
}

/** Which sensors may start a drag now. */
export interface DragGate {
  pointer: boolean
  keyboard: boolean
}

/** A sensor's options, plus whether it may start a drag now. */
type Gated<T> = T & { canStart: () => boolean }

export interface SensorOptions {
  pointer: Gated<PointerSensorOptions>
  keyboard: Gated<KeyboardSensorOptions>
  /** Swaps in where each arrow press goes. */
  setStep: (step: KeyboardCoordinateGetter) => void
  /** Turns either sensor on or off. */
  setGate: (gate: DragGate) => void
}

/**
 * Both sensors' options, made once. dnd-kit wants the same sensors on every render (it keys an effect
 * on them), so they stay mounted and these can change underneath them instead: the keyboard step, and
 * the gate each sensor checks before starting a drag. Both start off.
 */
export const createSensorOptions = (): SensorOptions => {
  let step: KeyboardCoordinateGetter = () => undefined
  let gate: DragGate = { pointer: false, keyboard: false }
  return {
    pointer: { ...POINTER_OPTIONS, canStart: () => gate.pointer },
    keyboard: {
      keyboardCodes: KEYBOARD_CODES,
      coordinateGetter: (event, args) => step(event, args),
      canStart: () => gate.keyboard
    },
    setStep: (next) => {
      step = next
    },
    setGate: (next) => {
      gate = next
    }
  }
}

/** Whether a sensor's gate (createSensorOptions) is open. Options without a gate never start a drag. */
const gateOpen = (options: object): boolean => 'canStart' in options && (options as Gated<object>).canStart()

const [startOnPointer] = PointerSensor.activators

/** dnd-kit's pointer sensor, started only while its gate (createSensorOptions) is open. */
export class SidebarPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (event: React.PointerEvent, options: PointerSensorOptions) =>
        gateOpen(options) && startOnPointer.handler(event, options)
    }
  ]
}

const [startOnKey] = KeyboardSensor.activators

/**
 * dnd-kit's keyboard sensor, started with Alt/Option + ↑/↓ rather than its default Enter or Space:
 * on a sidebar row those open the link, toggle the folder or (with ⌘/Shift) select. Once a drag is
 * on, the arrows move it, Enter or Space drops and Escape cancels. Only while its gate
 * (createSensorOptions) is open.
 */
export class SidebarKeyboardSensor extends KeyboardSensor {
  static activators: Activators<KeyboardSensorOptions> = [
    {
      eventName: 'onKeyDown',
      handler: (event: React.KeyboardEvent, options, context) =>
        event.altKey && gateOpen(options) && startOnKey.handler(event, options, context)
    }
  ]
}

/**
 * Ends whatever drag dnd-kit's sensors are running, as if the page had been hidden: both the
 * keyboard and the pointer sensor listen on the window for `visibilitychange` and cancel on it. Used
 * when the sidebar goes away mid-drag (unmount, hub switch), which dnd-kit doesn't handle itself: its
 * keyboard sensor would go on swallowing arrows, Enter and Space across the app. Dispatched on the
 * window only, so it never reaches the document-level `visibilitychange` listeners elsewhere in the
 * app (the notifications refresh), and those check `document.visibilityState` anyway, which it
 * doesn't change.
 */
export const cancelDndSensors = (): void => {
  window.dispatchEvent(new Event('visibilitychange'))
}

/** How far below and right of the pointer the drag preview's top-left sits, in pixels. */
export const PREVIEW_POINTER_GAP = 14
/** How far right a keyboard drag's preview sits, as a share of the row's width. */
export const PREVIEW_KEYBOARD_SHIFT = 0.6

/**
 * Where the drag preview goes, so it doesn't sit on the row the drag would drop on and hide its ring
 * or line. dnd-kit keeps the preview where the row was grabbed, which on a vertical drag is right over
 * the row under the pointer.
 * - A pointer drag: the preview's top-left goes just below and right of the pointer (as in Finder),
 *   from where the pointer went down plus how far it has moved.
 * - A keyboard drag: slid right by most of a row's width. A keyboard drag's collisions and zones are
 *   read from the preview's rect (a pointer drag reads the pointer, never the rect). Every row shares
 *   one centre x, so a sideways shift can't change which row or band it's over, and the drag still
 *   starts over the row picked up. Each arrow press aims the rect's own centre at a band, so the steps
 *   are unaffected too.
 */
export const previewTransform = (
  transform: Transform,
  activatorEvent: Event | null,
  activeNodeRect: ClientRect | null
): Transform => {
  if (!activeNodeRect) return transform
  if (activatorEvent instanceof MouseEvent) {
    return {
      ...transform,
      x: transform.x + activatorEvent.clientX - activeNodeRect.left + PREVIEW_POINTER_GAP,
      y: transform.y + activatorEvent.clientY - activeNodeRect.top + PREVIEW_POINTER_GAP
    }
  }
  if (activatorEvent instanceof KeyboardEvent) {
    return { ...transform, x: transform.x + activeNodeRect.width * PREVIEW_KEYBOARD_SHIFT }
  }
  return transform
}

/** previewTransform as a DragOverlay modifier. */
export const PREVIEW_MODIFIERS: Modifier[] = [
  ({ transform, activatorEvent, activeNodeRect }) => previewTransform(transform, activatorEvent, activeNodeRect)
]

/**
 * A pointer drag is over the row under the pointer, or, between and below the rows, the tree itself
 * (TREE_DROP_ID), which sits behind them all. A keyboard drag has no pointer, so it's over the row
 * nearest the dragged row; it never lands on the tree as a whole.
 */
export const collisionDetection: CollisionDetection = (args) => {
  const rows = args.droppableContainers.filter((container) => container.id !== TREE_DROP_ID)
  if (!args.pointerCoordinates) return closestCenter({ ...args, droppableContainers: rows })
  const overRow = pointerWithin({ ...args, droppableContainers: rows })
  if (overRow.length > 0) return overRow
  const tree = args.droppableContainers.filter((container) => container.id === TREE_DROP_ID)
  return pointerWithin({ ...args, droppableContainers: tree })
}

export interface Hover {
  overId: string
  zone: DropZone
}

export const sameHover = (a: Hover | null, b: Hover | null): boolean =>
  a === b || (a !== null && b !== null && a.overId === b.overId && a.zone === b.zone)

/**
 * How far down the viewport the drag is: the pointer for a pointer drag, the dragged row's centre for
 * a keyboard one. `pointerY` is the pointer as last seen; until it has moved, the start position plus
 * dnd-kit's delta stands in.
 */
const dragY = (event: DragMoveEvent, pointerY: number | null): number | null => {
  if (pointerY !== null) return pointerY
  const start = event.activatorEvent
  if (start instanceof MouseEvent) return start.clientY + event.delta.y
  const rect = event.active.rect.current.translated
  return rect ? rect.top + rect.height / 2 : null
}

/** The row the drag is over and which part of it (zoneFor), or null when it's over no row. */
export const hoverFrom = (
  event: DragMoveEvent,
  pointerY: number | null,
  isFolder: (id: string) => boolean
): Hover | null => {
  const { over } = event
  const y = dragY(event, pointerY)
  if (!over || y === null) return null
  const overId = String(over.id)
  const { top, height } = over.rect
  const offset = height > 0 ? Math.min(1, Math.max(0, (y - top) / height)) : 0.5
  return { overId, zone: zoneFor(offset, isFolder(overId)) }
}

/** One place a keyboard drag can stop: the middle of one band of a row (see zoneFor). */
export interface KeyboardBand {
  y: number
  overId: string
  zone: DropZone
}

export interface BandRow {
  id: string
  top: number
  height: number
  isFolder: boolean
}

const FOLDER_BANDS: [number, DropZone][] = [
  [1 / 8, 'before'],
  [1 / 2, 'into'],
  [7 / 8, 'after']
]
const RESOURCE_BANDS: [number, DropZone][] = [
  [1 / 4, 'before'],
  [3 / 4, 'after']
]

/** Every band of every row, top to bottom: a folder's quarter / half / quarter, a resource's halves. */
export const keyboardBands = (rows: BandRow[]): KeyboardBand[] =>
  rows
    .flatMap((row) =>
      (row.isFolder ? FOLDER_BANDS : RESOURCE_BANDS).map(([at, zone]) => ({
        y: row.top + row.height * at,
        overId: row.id,
        zone
      }))
    )
    .sort((a, b) => a.y - b.y)

/**
 * Where the next arrow press takes a keyboard drag that is now at `fromY`: the nearest band in
 * `direction` (1 down, -1 up) whose outcome differs from the current one, so every press lands
 * somewhere new. Bands that come to the same thing (after one row, before the next) are one stop.
 * Null when there's nowhere new that way.
 */
export const nextKeyboardStop = (
  bands: KeyboardBand[],
  fromY: number,
  direction: 1 | -1,
  outcomeOf: (band: KeyboardBand) => string
): KeyboardBand | null => {
  if (bands.length === 0) return null
  const nearest = bands.reduce((best, band) => (Math.abs(band.y - fromY) < Math.abs(best.y - fromY) ? band : best))
  const current = outcomeOf(nearest)
  const ahead =
    direction > 0
      ? bands.filter((band) => band.y > fromY + 0.5)
      : bands.filter((band) => band.y < fromY - 0.5).reverse()
  return ahead.find((band) => outcomeOf(band) !== current) ?? null
}

/**
 * `outcomeOf`, worked out once per `groupOf` key (dropGroups): bands that share a key are bound to come
 * to the same thing. Made afresh for each arrow press, as the tree may have changed in between.
 */
export const outcomePerGroup = (
  outcomeOf: (band: KeyboardBand) => string,
  groupOf: (overId: string, zone: DropZone) => string
): ((band: KeyboardBand) => string) => {
  const outcomes = new Map<string, string>()
  return (band) => {
    const key = groupOf(band.overId, band.zone)
    const known = outcomes.get(key)
    if (known !== undefined) return known
    const outcome = outcomeOf(band)
    outcomes.set(key, outcome)
    return outcome
  }
}

// The keys that end or cancel a drag, by code as the sensors read them.
const DRAG_KEYS = new Set([...KEYBOARD_CODES.end, ...KEYBOARD_CODES.cancel])
// A keyboard drag also owns the arrows: ↑/↓ move it (even with nowhere left to go, when the sensor
// doesn't take them), and Alt+←/→ would otherwise go back or forward a page.
const KEYBOARD_DRAG_KEYS = new Set([
  ...DRAG_KEYS,
  KeyboardCode.Up,
  KeyboardCode.Down,
  KeyboardCode.Left,
  KeyboardCode.Right
])

/** How long after the button comes up a click still belongs to the drag (dnd-kit's own wait). */
const CLICK_GRACE_MS = 50

/** The ways a press ends: released, or cancelled by the browser. */
const BUTTON_UP = ['pointerup', 'pointercancel'] as const

/**
 * What a drag needs from the page while it's on. Returns the teardown.
 * - The pointer's own position. dnd-kit's delta also counts any scrolling since the drag began, but
 *   `over.rect` is where the row is now, so the two only agree until the sidebar scrolls.
 * - The keys that drive the drag, marked handled (capture phase, so first), so they don't also
 *   follow the focused row's link or clear the selection. The rows skip their own key handling while
 *   a drag is on.
 * - For a pointer drag, the click that comes with letting go of the button, swallowed, so dropping a
 *   row back on itself doesn't follow its link and a drag cancelled with Escape doesn't click when
 *   the button is finally released. dnd-kit stops that click reaching React, but not the browser, and
 *   only for 50ms after the drag ends, however long the button stays down.
 */
export const startDragSession = (activatorEvent: Event, onPointerY: (y: number) => void): (() => void) => {
  const pointer = activatorEvent instanceof MouseEvent
  const keys = pointer ? DRAG_KEYS : KEYBOARD_DRAG_KEYS
  let buttonUp = false
  const onPointerMove = (event: PointerEvent): void => onPointerY(event.clientY)
  const onPointerUp = (): void => {
    buttonUp = true
  }
  const onKeyDown = (event: KeyboardEvent): void => {
    if (keys.has(event.code)) event.preventDefault()
  }
  const onClick = (event: MouseEvent): void => {
    event.preventDefault()
    event.stopPropagation()
  }
  if (pointer) {
    window.addEventListener('pointermove', onPointerMove, true)
    // A pointer the browser cancels (touch or pen, a system gesture) never sends pointerup.
    for (const type of BUTTON_UP) window.addEventListener(type, onPointerUp, true)
    window.addEventListener('click', onClick, true)
  }
  document.addEventListener('keydown', onKeyDown, true)

  return () => {
    document.removeEventListener('keydown', onKeyDown, true)
    if (!pointer) return
    window.removeEventListener('pointermove', onPointerMove, true)
    for (const type of BUTTON_UP) window.removeEventListener(type, onPointerUp, true)

    const disarm = (): void => {
      window.removeEventListener('click', onClick, true)
      window.removeEventListener('pointerdown', disarm, true)
      for (const type of BUTTON_UP) window.removeEventListener(type, release, true)
    }
    function release(): void {
      setTimeout(disarm, CLICK_GRACE_MS)
    }
    // A new press means the old one is over, however it ended (released outside the page, say):
    // its click is the user's, so the guard goes at once.
    window.addEventListener('pointerdown', disarm, true)
    // After a drop the button is already up. After Escape it may still be down: wait for it.
    if (buttonUp) release()
    else for (const type of BUTTON_UP) window.addEventListener(type, release, true)
  }
}
